import * as THREE from "three";
import { WallType, WallEdgePoint, WallEdgeConnection } from "../types/floorplannerTypes";
import type { FloorplannerStore } from "../store/floorplannerStore";
import { getArcTangentAtEndpoint, projectPointOntoArc } from "./arcUtils";

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - edgeConnectionHelpers called too early');
    }
  }
  return _floorplannerStore;
};

let editorStore: any = null;
const getEditorStore = () => {
  if (!editorStore) {
    try {
      // Deferred to avoid the editorStore -> edgeConnectionHelpers cycle.
      const module = require("../store/editorStore");
      editorStore = module.editorStore;
    } catch (error) {
      console.warn("edgeConnectionHelpers: Failed to load editorStore", error);
    }
  }
  return editorStore;
};

/**
 * Edge Connection Helpers - Geometric Calculations for Edge-Based Connections
 *
 * PURPOSE:
 * This file contains all geometric calculation utilities for the edge-based connection system.
 * It provides functions to calculate edge points, connection sides, gap distances, and validate connections.
 *
 * KEY CONCEPTS:
 * - Edge Points: Points on the edge of a wall (perpendicular offset from centerline)
 * - Connection Side: Which side of a wall (left/right) a connection occurs on
 * - Gap Distance: Distance between two edge points at a connection
 * - Edge Normal: Vector pointing outward from wall edge
 *
 * EDGE-BASED CONNECTIONS:
 * Instead of connecting walls at their centerlines (legacy approach), edge-based connections
 * connect walls at their edges. This preserves wall lengths and provides predictable UX.
 *
 * WHY EDGE POINTS:
 * - Wall length preservation: Walls don't extend to reach connections
 * - Visual clarity: Connections occur at actual wall edges
 * - Simple geometry: Walls remain 4-point rectangles
 * - Better performance: No complex intersection calculations
 *
 * RELATED FILES:
 * - snapToOtherObjects.ts: Snapping logic and validation
 * - jointPolygonGenerator.ts: Gap-filling polygons
 * - EDGE_CONNECTION_ARCHITECTURE.md: Complete system documentation
 *
 * @see EDGE_CONNECTION_ARCHITECTURE.md - Architecture documentation
 */

/**
 * Returns a unit vector representing the direction of the wall at the requested
 * endpoint (or at its midpoint, via projectedPoint). For straight walls this is the
 * chord direction. For arc walls (lineForm === "arc" with a controlPoint) this is
 * the Bezier tangent at the endpoint, so the wall's "local face" at that corner
 * follows the curve, matching how the wall is visually rendered.
 *
 * @param wall - The wall
 * @param position - Which point on the wall ("start", "end", "middle")
 * @param projectedPoint - For "middle" position, optional projected point on centerline
 * @returns Unit direction vector (THREE.Vector2). Falls back to chord direction when
 *          tangent cannot be derived or when wall is not an arc.
 */
export function getWallDirectionAt(
  wall: WallType,
  position: "start" | "end" | "middle",
  projectedPoint?: THREE.Vector2
): THREE.Vector2 {
  const chord = new THREE.Vector2(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  const chordLength = chord.length();
  const chordDir = chordLength > 1e-6 ? chord.clone().normalize() : new THREE.Vector2(1, 0);

  const isArc = wall.lineForm === "arc" && wall.controlPoint !== undefined;
  if (!isArc || !wall.controlPoint) {
    return chordDir;
  }

  const p0 = wall.start;
  const p1 = wall.controlPoint;
  const p2 = wall.end;

  // Quadratic Bezier derivative: B'(t) = 2(1-t)(P1-P0) + 2t(P2-P1)
  // For "start" (t=0): direction from start toward control point.
  // For "end"   (t=1): direction from control point toward end.
  // For "middle": derivative at the t nearest to projectedPoint (if provided),
  //   otherwise at t=0.5.
  let tangent: THREE.Vector2;
  if (position === "start") {
    tangent = new THREE.Vector2(p1.x - p0.x, p1.y - p0.y);
  } else if (position === "end") {
    tangent = new THREE.Vector2(p2.x - p1.x, p2.y - p1.y);
  } else {
    let t = 0.5;
    if (projectedPoint && chordLength > 1e-6) {
      // Approximate t by projecting onto the chord (good enough for side/perpendicular
      // selection given typical curvatures). Clamped to [0,1].
      const rel = new THREE.Vector2(projectedPoint.x - p0.x, projectedPoint.y - p0.y);
      t = Math.max(0, Math.min(1, rel.dot(chordDir) / chordLength));
    }
    const oneMinusT = 1 - t;
    tangent = new THREE.Vector2(
      2 * oneMinusT * (p1.x - p0.x) + 2 * t * (p2.x - p1.x),
      2 * oneMinusT * (p1.y - p0.y) + 2 * t * (p2.y - p1.y)
    );
  }

  if (tangent.lengthSq() < 1e-12) {
    return chordDir;
  }
  return tangent.normalize();
}

/**
 * Calculates the edge point and normal for a wall at a specific position.
 * Edge points are positioned at the wall's edge (perpendicular to wall direction),
 * which allows walls to connect without changing their lengths.
 *
 * For arc walls, the perpendicular at the endpoint is derived from the Bezier
 * tangent at that endpoint, not the chord. This matches the wall's visual face
 * direction at the corner and keeps snapping aligned with rendering.
 *
 * @param wall - The wall to calculate edge point for
 * @param position - Which position to use ("start", "end", or "middle")
 * @param connectionAngle - The angle of the connecting wall in radians
 * @param wallWidth - The wall width (uses wall.wallWidth or fallback value if not provided)
 * @param projectedPoint - For "middle" position, use this projected point instead of midpoint (T-junctions)
 * @param forceSide - Force a specific side ("left", "right") or "center" (no offset). If not provided, auto-determines side
 * @returns WallEdgePoint containing position, normal, and side information
 */
export function calculateWallEdgePoint(
  wall: WallType,
  position: "start" | "end" | "middle",
  connectionAngle: number,
  wallWidth?: number,
  projectedPoint?: THREE.Vector2,
  forceSide?: "left" | "right" | "center"
): WallEdgePoint {
  // Use the wall's local direction AT the requested endpoint so that arc walls
  // produce corners that follow their Bezier tangent (matching the rendered outline),
  // not the chord.
  const wallDirection = getWallDirectionAt(wall, position, projectedPoint);

  // Check for zero-length wall (both start==end and tangent could not be recovered)
  const chordLength = new THREE.Vector2(wall.end.x - wall.start.x, wall.end.y - wall.start.y).length();
  if (chordLength < 1e-6 && wallDirection.lengthSq() < 1e-6) {
    // Return a safe default for degenerate walls
    // Use a default upward normal for the edge
    const defaultNormal = new THREE.Vector2(0, 1);
    const edgePosition = new THREE.Vector2(
      (wall.start.x + wall.end.x) / 2,
      (wall.start.y + wall.end.y) / 2
    );
    return {
      wallId: wall.id,
      endpoint: position,
      edgePosition,
      edgeNormal: defaultNormal,
      side: "left", // Default side
    };
  }

  // Calculate perpendicular vector (rotate direction 90° counterclockwise)
  const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

  // Get wall width (use wall.wallWidth or floorplannerStore default)
  const width = wallWidth !== undefined ? wallWidth : (wall.wallWidth || getFloorplannerStore().wallWidth);

  // Determine which side (left or right) the connection should occur on
  // Use forceSide if provided, otherwise auto-determine
  const side = forceSide && forceSide !== "center"
    ? forceSide
    : determineConnectionSide(wallDirection, connectionAngle);

  const clonePoint = (point: THREE.Vector2 | { x: number; y: number }) =>
    (point as THREE.Vector2).clone ? (point as THREE.Vector2).clone() : new THREE.Vector2(point.x, point.y);

  // Calculate base position on wall centerline
  // For "start" and "end", use the endpoint
  // For "middle", use the projected point if provided, otherwise use midpoint
  let basePosition: THREE.Vector2;
  if (position === "start") {
    basePosition = clonePoint(wall.start);
  } else if (position === "end") {
    basePosition = clonePoint(wall.end);
  } else {
    // "middle" - use projected point if provided (T-junction at specific position),
    // otherwise fall back to midpoint
    if (projectedPoint) {
      basePosition = clonePoint(projectedPoint);
    } else {
      basePosition = new THREE.Vector2(
        (wall.start.x + wall.end.x) / 2,
        (wall.start.y + wall.end.y) / 2
      );
    }
  }

  // Calculate edge position
  // For "center" forceSide, use the base position without offset
  const edgePosition = forceSide === "center"
    ? basePosition.clone()
    : new THREE.Vector2(
        basePosition.x + perpendicular.x * (width / 2) * (side === "left" ? 1 : -1),
        basePosition.y + perpendicular.y * (width / 2) * (side === "left" ? 1 : -1)
      );

  // Calculate edge normal: perpendicular * side (points outward from wall)
  const edgeNormal = new THREE.Vector2(
    perpendicular.x * (side === "left" ? 1 : -1),
    perpendicular.y * (side === "left" ? 1 : -1)
  );

  return {
    wallId: wall.id,
    endpoint: position,
    edgePosition,
    edgeNormal,
    side,
  };
}

/**
 * Determines which side of a wall (left or right) a connection should occur on
 * based on the relative angle between the wall and the connection.
 *
 * @param wallDirection - Normalized direction vector of the wall
 * @param connectionAngle - Angle of the connecting wall in radians
 * @returns "left" or "right" indicating which side to connect
 */
export function determineConnectionSide(
  wallDirection: THREE.Vector2,
  connectionAngle: number
): "left" | "right" {
  // Calculate the angle of the wall direction vector
  const wallAngle = Math.atan2(wallDirection.y, wallDirection.x);

  // Calculate the relative angle between wall direction and connection angle
  let relativeAngle = connectionAngle - wallAngle;

  // Normalize angle to range [-π, π]
  while (relativeAngle > Math.PI) {
    relativeAngle -= 2 * Math.PI;
  }
  while (relativeAngle < -Math.PI) {
    relativeAngle += 2 * Math.PI;
  }

  // If relative angle is positive (counterclockwise), return "left"
  // If relative angle is negative (clockwise), return "right"
  // If relative angle is exactly 0, use wall id hash for stable tie-breaking
  if (relativeAngle === 0) {
    // Use wall direction hash for deterministic tie-breaking
    const wallHash = wallDirection.x * 1000 + wallDirection.y;
    return wallHash > 0 ? "right" : "left";
  }
  return relativeAngle >= 0 ? "left" : "right";
}

/**
 * Calculates the edge normal vector for a wall endpoint.
 * The edge normal points outward from the wall and is used for
 * determining connection sides and gap calculations.
 *
 * @param wall - The wall to calculate edge normal for
 * @param endpoint - Which endpoint to use (start or end)
 * @param side - Which side of the wall (left or right)
 * @returns Normalized THREE.Vector2 representing the edge normal
 */
export function calculateEdgeNormal(
  wall: WallType,
  endpoint: "start" | "end",
  side: "left" | "right"
): THREE.Vector2 {
  // Calculate wall direction vector
  const wallDirectionVec = new THREE.Vector2(
    wall.end.x - wall.start.x,
    wall.end.y - wall.start.y
  );

  // Check for zero-length wall
  const wallLength = wallDirectionVec.length();
  if (wallLength < 1e-6) {
    // Return a default normal for degenerate walls
    // Use upward direction as fallback
    return new THREE.Vector2(0, 1);
  }

  const wallDirection = wallDirectionVec.normalize();

  // Calculate perpendicular vector (90° rotation)
  const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

  // Multiply by +1 for left side, -1 for right side
  return new THREE.Vector2(
    perpendicular.x * (side === "left" ? 1 : -1),
    perpendicular.y * (side === "left" ? 1 : -1)
  ).normalize();
}

/**
 * Calculates the angle of a wall in radians.
 * The angle represents the direction from start to end point.
 *
 * @param wall - The wall to calculate angle for
 * @returns Angle in radians
 */
export function getWallAngle(wall: WallType): number {
  return Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
}

/**
 * Returns the angle of a wall at a specific endpoint, using the tangent direction
 * for arc walls and the chord direction for straight walls.
 */
export function getWallAngleAtEndpoint(wall: WallType, endpoint: "start" | "end"): number {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const tangent = getArcTangentAtEndpoint(wall, endpoint);
    return Math.atan2(tangent.y, tangent.x);
  }
  return getWallAngle(wall);
}

/**
 * Calculates the gap distance between two edge points.
 * This distance is used for determining if a gap polygon is needed
 * and for proper rendering of wall fills.
 *
 * @param sourceEdgePoint - Position on source wall edge
 * @param targetEdgePoint - Position on target wall edge
 * @returns Euclidean distance between the two points
 */
export function calculateGapDistance(
  sourceEdgePoint: THREE.Vector2,
  targetEdgePoint: THREE.Vector2
): number {
  return sourceEdgePoint.distanceTo(targetEdgePoint);
}

const closestPointOnSegment = (
  point: THREE.Vector2,
  a: THREE.Vector2,
  b: THREE.Vector2,
): THREE.Vector2 => {
  const ab = new THREE.Vector2().subVectors(b, a);
  const abLengthSq = ab.lengthSq();
  if (abLengthSq < 1e-12) {
    return a.clone();
  }

  const t = THREE.MathUtils.clamp(
    new THREE.Vector2().subVectors(point, a).dot(ab) / abLengthSq,
    0,
    1,
  );
  return a.clone().addScaledVector(ab, t);
};

const areWallDirectionsCollinear = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end",
): boolean => {
  const sourceDirection = getWallDirectionAt(sourceWall, sourceEndpoint);
  const targetDirection = getWallDirectionAt(targetWall, targetEndpoint);
  const cross = sourceDirection.x * targetDirection.y - sourceDirection.y * targetDirection.x;
  return Math.abs(cross) < 0.05;
};

const getCollinearEndpointCapTargetPoint = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  sourceEdgePosition: THREE.Vector2,
  targetWall: WallType,
  targetProjectedPoint?: THREE.Vector2,
): THREE.Vector2 | null => {
  if (!targetProjectedPoint) {
    return null;
  }

  const endpointTolerance = 1e-6;
  const capEndpoint =
    targetProjectedPoint.distanceTo(targetWall.start) <= endpointTolerance
      ? "start"
      : targetProjectedPoint.distanceTo(targetWall.end) <= endpointTolerance
        ? "end"
        : null;
  if (!capEndpoint || !areWallDirectionsCollinear(sourceWall, sourceEndpoint, targetWall, capEndpoint)) {
    return null;
  }

  const capLeft = calculateWallEdgePoint(
    targetWall,
    capEndpoint,
    getWallAngleAtEndpoint(sourceWall, sourceEndpoint),
    undefined,
    undefined,
    "left",
  ).edgePosition;
  const capRight = calculateWallEdgePoint(
    targetWall,
    capEndpoint,
    getWallAngleAtEndpoint(sourceWall, sourceEndpoint),
    undefined,
    undefined,
    "right",
  ).edgePosition;

  return closestPointOnSegment(sourceEdgePosition, capLeft, capRight);
};

const getEndpointCapSegment = (
  wall: WallType,
  endpoint: "start" | "end",
): { left: THREE.Vector2; right: THREE.Vector2 } => ({
  left: calculateWallEdgePoint(
    wall,
    endpoint,
    0,
    undefined,
    undefined,
    "left",
  ).edgePosition,
  right: calculateWallEdgePoint(
    wall,
    endpoint,
    0,
    undefined,
    undefined,
    "right",
  ).edgePosition,
});

const getCollinearEndpointCapOverlapPoints = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  sourceEdgePosition: THREE.Vector2,
  targetWall: WallType,
  targetEndpoint: "start" | "end",
  targetEdgePosition: THREE.Vector2,
): { source: THREE.Vector2; target: THREE.Vector2 } | null => {
  if (!areWallDirectionsCollinear(sourceWall, sourceEndpoint, targetWall, targetEndpoint)) {
    return null;
  }

  const sourceWidth = sourceWall.wallWidth ?? getFloorplannerStore().wallWidth;
  const targetWidth = targetWall.wallWidth ?? getFloorplannerStore().wallWidth;
  const widthTolerance = 1e-6;

  if (sourceWidth > targetWidth + widthTolerance) {
    const sourceCap = getEndpointCapSegment(sourceWall, sourceEndpoint);
    return {
      source: closestPointOnSegment(targetEdgePosition, sourceCap.left, sourceCap.right),
      target: targetEdgePosition.clone(),
    };
  }

  if (targetWidth > sourceWidth + widthTolerance) {
    const targetCap = getEndpointCapSegment(targetWall, targetEndpoint);
    return {
      source: sourceEdgePosition.clone(),
      target: closestPointOnSegment(sourceEdgePosition, targetCap.left, targetCap.right),
    };
  }

  return null;
};

/**
 * Checks if a connection position represents an endpoint
 * @param position - Connection position (0 or 10000 for endpoints, other for mid-points)
 * @returns true if position is at an endpoint
 */
export function isEndpointConnectionPosition(position: number): boolean {
  return position === 0 || position === 10000;
}

/**
 * Converts connection position to endpoint string
 * @param position - Connection position (0 for start, 10000 for end, or other for mid-point)
 * @returns "start" | "end" | null
 */
export function connectionPositionToEndpoint(position: number): "start" | "end" | null {
  if (position === 0) {
    return "start";
  } else if (position === 10000) {
    return "end";
  }
  return null; // Mid-point connection
}

/**
 * Converts endpoint string to connection position
 * @param endpoint - Endpoint string ("start" or "end")
 * @returns 0 for start, 10000 for end
 */
export function endpointToConnectionPosition(endpoint: "start" | "end"): number {
  return endpoint === "start" ? 0 : 10000;
}

/**
 * Calculate distance between two wall endpoints.
 * Used for overlap validation in edge snapping.
 *
 * @param wall1 - First wall
 * @param endpoint1 - Which endpoint of wall1
 * @param wall2 - Second wall
 * @param endpoint2 - Which endpoint of wall2
 * @returns Euclidean distance between the two endpoints
 */
export function calculateEndpointDistance(
  wall1: WallType,
  endpoint1: "start" | "end",
  wall2: WallType,
  endpoint2: "start" | "end"
): number {
  const pos1 = endpoint1 === "start" ? wall1.start : wall1.end;
  const pos2 = endpoint2 === "start" ? wall2.start : wall2.end;
  return pos1.distanceTo(pos2);
}

/**
 * Find which position (start, end, or middle) of a target wall is closest to a source position.
 * Used to determine which position to connect to during edge snapping for T-junctions.
 *
 * @param sourcePosition - Position to measure from
 * @param targetWall - Wall to find closest position on
 * @returns Object containing the closest position and its distance
 */
export function getClosestPosition(
  sourcePosition: THREE.Vector2,
  targetWall: WallType
): { position: "start" | "end" | "middle"; distance: number; projectedPoint?: THREE.Vector2 } {
  const distToStart = sourcePosition.distanceTo(targetWall.start);
  const distToEnd = sourcePosition.distanceTo(targetWall.end);

  // Define endpoint tolerance - if source is within this distance of an endpoint,
  // treat it as an endpoint-to-endpoint connection, not a T-junction
  const editor = getEditorStore ? getEditorStore() : undefined;
  const endpointTolerance = Math.min(0.1, Math.max(0.02, (editor?.snappingTolerance ?? 0.05) * 1.2));

  // Check if source is close enough to start or end to be considered an endpoint connection
  if (distToStart < endpointTolerance) {
    return { position: "start", distance: distToStart };
  }
  if (distToEnd < endpointTolerance) {
    return { position: "end", distance: distToEnd };
  }

  // Source is not near an endpoint - this is a T-junction to the middle of the wall
  // For arc walls, project onto the curve instead of the straight centerline
  if (targetWall.lineForm === "arc" && targetWall.controlPoint) {
    const projection = projectPointOntoArc(sourcePosition, targetWall.start, targetWall.controlPoint, targetWall.end);
    return { position: "middle", distance: projection.distance, projectedPoint: projection.point };
  }

  // Project source position onto the target wall's centerline to find the connection point
  const wallDirection = new THREE.Vector2(
    targetWall.end.x - targetWall.start.x,
    targetWall.end.y - targetWall.start.y
  );
  const wallLength = wallDirection.length();

  if (wallLength < 1e-6) {
    // Degenerate wall - use midpoint
    const middle = new THREE.Vector2(
      (targetWall.start.x + targetWall.end.x) / 2,
      (targetWall.start.y + targetWall.end.y) / 2
    );
    return { position: "middle", distance: sourcePosition.distanceTo(middle), projectedPoint: middle };
  }

  wallDirection.normalize();

  // Vector from wall start to source position
  const toSource = new THREE.Vector2(
    sourcePosition.x - targetWall.start.x,
    sourcePosition.y - targetWall.start.y
  );

  // Project toSource onto wall direction to get the parameter t (position along wall)
  const t = toSource.dot(wallDirection);

  // Clamp t to [0, wallLength] to keep projection on the wall segment
  const clampedT = Math.max(0, Math.min(wallLength, t));

  // Calculate the projected point on the wall's centerline
  const projectedPoint = new THREE.Vector2(
    targetWall.start.x + wallDirection.x * clampedT,
    targetWall.start.y + wallDirection.y * clampedT
  );

  // Calculate distance from source to projected point
  const distToProjection = sourcePosition.distanceTo(projectedPoint);

  return { position: "middle", distance: distToProjection, projectedPoint };
}

/**
 * Find which endpoint of a target wall is closest to a source position.
 * Used to determine which endpoint to connect to during edge snapping.
 *
 * @param sourcePosition - Position to measure from
 * @param targetWall - Wall to find closest endpoint on
 * @returns Object containing the closest endpoint and its distance
 */
export function getClosestEndpoint(
  sourcePosition: THREE.Vector2,
  targetWall: WallType
): { endpoint: "start" | "end"; distance: number } {
  const distToStart = sourcePosition.distanceTo(targetWall.start);
  const distToEnd = sourcePosition.distanceTo(targetWall.end);

  return {
    endpoint: distToStart <= distToEnd ? "start" : "end",
    distance: Math.min(distToStart, distToEnd),
  };
}

/**
 * Check if connecting two walls at given endpoints would cause overlap.
 * An overlap occurs when a wall is longer than the distance to the other wall's opposite endpoint.
 * This prevents walls from extending beyond their connection points.
 *
 * GEOMETRIC REASONING:
 * When two walls connect endpoint-to-endpoint, they should meet at their endpoints.
 * If the source wall is longer than the distance from its endpoint to the target's
 * opposite endpoint, the source wall would have to "wrap around" or overlap.
 *
 * EXAMPLE:
 * Wall A: 15 units long, connecting to Wall B's start (which is at position 0)
 * Wall B: 10 units long, extending from position 0 to 10
 * Distance from Wall A's start to Wall B's end: 10 units
 * Wall A length (15) > Distance (10) = OVERLAP
 *
 * @param sourceWall - Wall being moved/positioned
 * @param sourceEndpoint - Which endpoint of source wall
 * @param targetWall - Wall being connected to
 * @param targetEndpoint - Which endpoint of target wall
 * @returns true if connecting would cause overlap, false otherwise
 */
export function wouldWallsOverlap(
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end"
): boolean {
  // Calculate the total length of the source wall
  // This is the distance from start to end
  const sourceWallLength = sourceWall.start.distanceTo(sourceWall.end);

  // Get the position of the source wall's endpoint being connected
  const sourcePos = sourceEndpoint === "start" ? sourceWall.start : sourceWall.end;

  // Get the position of the target wall's OPPOSITE endpoint
  // (the endpoint not being connected to)
  // This is the farthest point the source wall could reach without wrapping
  const targetOtherPos = targetEndpoint === "start" ? targetWall.end : targetWall.start;

  // Calculate the distance from the source endpoint to the target's opposite endpoint
  // This is the maximum length the source wall can have without overlapping
  const distToOtherEnd = sourcePos.distanceTo(targetOtherPos);

  // If the source wall is longer than this distance, it would overlap
  // Return true to indicate the connection should be prevented
  return sourceWallLength > distToOtherEnd;
}

/**
 * Creates a complete edge connection object between two walls.
 * This function calculates all necessary geometric properties for
 * an edge-to-edge connection and returns a WallEdgeConnection object.
 *
 * @param sourceWall - The source wall for the connection
 * @param sourceEndpoint - Which endpoint on the source wall
 * @param targetWall - The target wall for the connection
 * @param targetEndpoint - Which endpoint or position on the target wall
 * @param targetProjectedPoint - For T-junctions, the projected point on target wall's centerline
 * @param forceSide - Force a specific connection side ("left", "right", or "center")
 * @returns Complete WallEdgeConnection object
 */
type EdgeSide = "left" | "right" | "center";
type EdgeForceSideOption = EdgeSide | {
  sourceSide?: EdgeSide;
  targetSide?: EdgeSide;
};

const normalizeForceSides = (option?: EdgeForceSideOption): { sourceSide?: EdgeSide; targetSide?: EdgeSide } => {
  if (!option) {
    return {};
  }
  if (typeof option === "string") {
    const normalizedSource = option === "center" ? undefined : option;
    return {
      sourceSide: normalizedSource,
      targetSide: option,
    };
  }
  return {
    sourceSide: option.sourceSide === "center" ? undefined : option.sourceSide,
    targetSide: option.targetSide,
  };
};

export function createEdgeConnection(
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end" | "middle",
  targetProjectedPoint?: THREE.Vector2,
  forceSide?: EdgeForceSideOption
): WallEdgeConnection {
  const { sourceSide, targetSide } = normalizeForceSides(forceSide);

  // Calculate source edge point
  // Use the target wall's orientation at the connected endpoint (tangent for arcs,
  // chord for straight walls) to determine which side the connection should be on.
  // For T-junctions ("middle") fall back to the target wall's chord angle.
  const sourceAngle = targetEndpoint === "middle" ? getWallAngle(targetWall) : getWallAngleAtEndpoint(targetWall, targetEndpoint);
  const sourceEdgePoint = calculateWallEdgePoint(sourceWall, sourceEndpoint, sourceAngle, undefined, undefined, sourceSide);

  // Calculate target edge point using the source wall's orientation at its
  // connecting endpoint. This keeps the connection symmetric and, for arc walls,
  // uses the Bezier tangent at that endpoint (via getWallAngleAtEndpoint).
  const targetAngle = getWallAngleAtEndpoint(sourceWall, sourceEndpoint);
  const resolvedTargetSide = targetSide !== undefined ? targetSide : sourceSide;
  const targetEdgePoint = calculateWallEdgePoint(targetWall, targetEndpoint, targetAngle, undefined, targetProjectedPoint, resolvedTargetSide);
  const capTargetPoint = targetEndpoint === "middle"
    ? getCollinearEndpointCapTargetPoint(
        sourceWall,
        sourceEndpoint,
        sourceEdgePoint.edgePosition,
        targetWall,
        targetProjectedPoint,
      )
    : null;
  if (capTargetPoint) {
    targetEdgePoint.edgePosition = capTargetPoint;
  }
  const capOverlapPoints = targetEndpoint === "start" || targetEndpoint === "end"
    ? getCollinearEndpointCapOverlapPoints(
        sourceWall,
        sourceEndpoint,
        sourceEdgePoint.edgePosition,
        targetWall,
        targetEndpoint,
        targetEdgePoint.edgePosition,
      )
    : null;
  if (capOverlapPoints) {
    sourceEdgePoint.edgePosition = capOverlapPoints.source;
    targetEdgePoint.edgePosition = capOverlapPoints.target;
  }

  // Calculate gap distance between edge points
  const gapDistance = calculateGapDistance(sourceEdgePoint.edgePosition, targetEdgePoint.edgePosition);

  // Calculate connection angle (angle between the two walls) using endpoint-aware angles
  const angleA = getWallAngleAtEndpoint(sourceWall, sourceEndpoint);
  const angleB = targetEndpoint === "middle" ? getWallAngle(targetWall) : getWallAngleAtEndpoint(targetWall, targetEndpoint);
  const delta = Math.abs(angleA - angleB) % (2 * Math.PI);
  // Normalize to [0, π] range (0° to 180°)
  // This ensures consistent angle regardless of wall direction
  const connectionAngle = delta > Math.PI ? 2 * Math.PI - delta : delta;

  // Return complete connection object
  // This object contains all information needed to:
  // 1. Render the connection
  // 2. Generate joint polygons
  // 3. Validate the connection
  // 4. Update the connection if walls move
  return {
    id: targetWall.id, // Use targetWallId as the id for consistency with legacy connections
    sourceWallId: sourceWall.id,
    targetWallId: targetWall.id,
    sourceEndpoint,
    targetEndpoint,
    sourceSide: sourceEdgePoint.side,
    targetSide: targetEdgePoint.side,
    sourceEdgePoint: sourceEdgePoint.edgePosition,
    targetEdgePoint: targetEdgePoint.edgePosition,
    gapDistance,
    connectionAngle,
  };
}

/**
 * Maximum edge-to-edge gap still considered a real join.
 *
 * Real joins have near-zero gap after side selection. Allow half the thicker
 * wall (+ snap slack) for width-mismatch / angled corners. Full-width gaps
 * (e.g. 0.5 m between 0.5 m walls) are open space — keeping them produces
 * joint-fill "squares" across the void.
 */
export function getMaxValidEdgeJoinGap(
  sourceWall: WallType,
  targetWall: WallType,
  defaultWallWidth?: number,
): number {
  let resolvedDefault = defaultWallWidth;
  if (resolvedDefault === undefined) {
    try {
      resolvedDefault = getFloorplannerStore().wallWidth;
    } catch {
      resolvedDefault = 0.2;
    }
  }
  const sourceWidth = sourceWall.wallWidth ?? resolvedDefault;
  const targetWidth = targetWall.wallWidth ?? resolvedDefault;
  const snapTolerance = getEditorStore()?.snappingTolerance ?? 0.2;
  return Math.max(sourceWidth, targetWidth, 0.01) * 0.5 + Math.max(snapTolerance, 0);
}

/** True when a (re)computed connection still meets within join range. */
export function isEdgeConnectionWithinJoinRange(
  connection: Pick<WallEdgeConnection, "gapDistance">,
  sourceWall: WallType,
  targetWall: WallType,
): boolean {
  return Number.isFinite(connection.gapDistance) &&
    connection.gapDistance >= 0 &&
    connection.gapDistance <= getMaxValidEdgeJoinGap(sourceWall, targetWall);
}

/**
 * Pick which edge joins to keep at a wall.
 *
 * Safer 1612 policy (same-target dedupe only):
 * per sourceEndpoint start/end, at most one join per target wall
 * (keep smallest gapDistance; prefer endpoint-to-endpoint over middle on ties).
 *
 * Does NOT drop joins to other walls — that made snapping/multi-corners too hard.
 * Cross-wall gap limits remain in {@link isEdgeConnectionWithinJoinRange} (1608).
 *
 * Connections whose sourceEndpoint is not start/end are left unchanged.
 */
export function selectPrimaryEdgeConnections(
  connections: readonly WallEdgeConnection[],
): WallEdgeConnection[] {
  const passthrough: WallEdgeConnection[] = [];
  const byEndpoint = new Map<"start" | "end", WallEdgeConnection[]>();

  for (const connection of connections) {
    if (connection.sourceEndpoint === "start" || connection.sourceEndpoint === "end") {
      const list = byEndpoint.get(connection.sourceEndpoint) ?? [];
      list.push(connection);
      byEndpoint.set(connection.sourceEndpoint, list);
    } else {
      passthrough.push(connection);
    }
  }

  const selected: WallEdgeConnection[] = [...passthrough];

  byEndpoint.forEach((endpointConnections) => {
    const bestByTarget = new Map<string, WallEdgeConnection>();
    for (const connection of endpointConnections) {
      const existing = bestByTarget.get(connection.targetWallId);
      if (
        !existing ||
        connection.gapDistance < existing.gapDistance ||
        (connection.gapDistance === existing.gapDistance &&
          connection.targetEndpoint !== "middle" &&
          existing.targetEndpoint === "middle")
      ) {
        // Prefer endpoint-to-endpoint over endpoint-to-middle when gaps tie.
        bestByTarget.set(connection.targetWallId, connection);
      }
    }
    selected.push(...bestByTarget.values());
  });

  return selected;
}

/**
 * Returns edge connections on the wall that should be removed by
 * {@link selectPrimaryEdgeConnections}.
 */
export function findNonPrimaryEdgeConnections(
  connections: readonly WallEdgeConnection[],
): WallEdgeConnection[] {
  const keep = new Set(selectPrimaryEdgeConnections(connections));
  return connections.filter((connection) => !keep.has(connection));
}

export function recomputeEdgeConnectionForCurrentGeometry(
  sourceWall: WallType,
  targetWall: WallType,
  oldConnection: WallEdgeConnection,
): WallEdgeConnection | null {
  const sourceEndpoint = oldConnection.sourceEndpoint as "start" | "end";
  const sidePairs = [
    { sourceSide: "left", targetSide: "left" },
    { sourceSide: "left", targetSide: "right" },
    { sourceSide: "right", targetSide: "left" },
    { sourceSide: "right", targetSide: "right" },
  ] as const;

  let bestConnection: WallEdgeConnection | null = null;
  let bestGap = Number.POSITIVE_INFINITY;

  for (const sidePair of sidePairs) {
    const sourceReference = calculateWallEdgePoint(
      sourceWall,
      sourceEndpoint,
      0,
      undefined,
      undefined,
      sidePair.sourceSide,
    ).edgePosition;
    const projectedPoint = oldConnection.targetEndpoint === "middle"
      ? (() => {
        const closest = getClosestPosition(sourceReference, targetWall);
        return closest.projectedPoint ??
          (closest.position === "start"
            ? targetWall.start.clone()
            : closest.position === "end"
              ? targetWall.end.clone()
              : undefined);
      })()
      : undefined;

    const candidate = createEdgeConnection(
      sourceWall,
      sourceEndpoint,
      targetWall,
      oldConnection.targetEndpoint,
      projectedPoint,
      sidePair,
    );

    if (candidate.gapDistance < bestGap) {
      bestGap = candidate.gapDistance;
      bestConnection = candidate;
    }
  }

  if (!bestConnection) {
    return null;
  }

  return {
    ...bestConnection,
    id: oldConnection.id,
    voidFill: oldConnection.voidFill ? { ...oldConnection.voidFill } : undefined,
  };
}
